/**
 * More than just api requests, here we're performing actions
 * that depend on opportunity related objects state
 */

import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  fetchItem,
  fetchItems,
  postItem,
  putItem,
} from '../../support/queries';
import { OpportunityProduct, Opportunity } from '../../types/opportunity';
import { CacheKey } from '../../types/cache-keys';
import { OpportunityPoV } from '../../types/opportunity-pov-items';
import { queryClient } from '../../support/queryClient';

/**
 * One-time only for each opportunity
 * Copy outcomes into pov-outcome records
 * Based on the opportunity outcomes_init property
 * Called upon moving to design phase
 */
const initPovOutcomes = async (opportunity) => {
  const oppUrl = `db/opportunity/${opportunity.Id}`;
  const dbOpportunity = await fetchItem(oppUrl);
  const pov = await fetchPov(opportunity.Id);
  if (!pov) {
    console.error("Couldn't get PoV for opportunity");
    return;
  }
  if (dbOpportunity?.outcomes_init !== false) {
    return;
  }
  const oppOutcomesUrl = `db/opportunity-outcome`;
  const oppOutcomesPayload = { opportunity: opportunity?.Id ?? opportunity.id };
  const oppOutcomes = await queryClient.fetchQuery({
    queryKey: [oppOutcomesUrl, oppOutcomesPayload],
    queryFn: () => fetchItems(oppOutcomesUrl, oppOutcomesPayload),
  });
  await Promise.all(
    oppOutcomes?.map((outcome) => {
      return queryClient.getMutationCache().build(queryClient, {
        mutationFn: () =>
          postItem('db/pov-outcome', {
            title: outcome.outcome.name,
            description: outcome.outcome.description,
            pov: pov.id,
          }),
        mutationKey: [CacheKey.PovOutcomes, pov.id],
      }).execute(undefined);
    })
  );
  await putItem('db/opportunity', {
    ...dbOpportunity,
    outcomes_init: true,
  });
  queryClient.invalidateQueries({ queryKey: [CacheKey.Opportunity, opportunity?.Id ?? opportunity.id] });
  queryClient.invalidateQueries({ queryKey: [CacheKey.Pov, opportunity?.Id ?? opportunity.id] });
  queryClient.invalidateQueries({ queryKey: [CacheKey.PovOutcomes, pov.id.toString()] });
};

/**
 * dev data at least has multiple pov objects for each opportunity
 * eventually we'll clean this up, but for now
 * - only add a pov if none exists for that opportunity
 * - take the last pov in the case where we already have 1 or more
 */
const fetchPov = async (opportunityId: Opportunity['id'|'Id']): Promise<OpportunityPoV | null> => {
  const cachedPov: OpportunityPoV = queryClient.getQueryData([CacheKey.Pov, opportunityId]);
  if (cachedPov) {
    return cachedPov;
  }

  const url = 'db/pov';
  const payload = { opportunity: opportunityId };
  const povs: OpportunityPoV[] = await fetchItems(url, payload);
  if (povs?.length > 0) {
    return povs.slice(-1)[0];
  }
  if (povs?.length === 0) {
    const opportunityData: Opportunity = queryClient.getQueryData([CacheKey.Opportunity, opportunityId]);
    const name = opportunityData?.Name ?? `PoV for Opportunity ${opportunityId}`;
    const newPOV = {
      name,
      opportunity: opportunityId
    };
    console.warn('adding new pov', newPOV);
    const pov = await postItem('db/pov', newPOV);
    return pov;
  }
  return null;
};

type OpportunityLike = Partial<Opportunity> & ({ id: string } | { Id: string });

const useFetchPov = (opportunity: OpportunityLike, options: Record<string, any> = {}) => {
  const id = opportunity?.Id ?? opportunity?.id;
  return useQuery({
    queryKey: [CacheKey.Pov, id],
    queryFn: () => fetchPov(id),
    ...{
        ...options,
        enabled: Object.keys(options).includes('enabled') ? options.enabled && !!id : !!id
      },
  });
};

const isPovStarted = (product: OpportunityProduct): boolean => {
  if (!product?.pov_kickoff_date) {
    return false;
  }
  const kickoffDate = new Date(product?.pov_kickoff_date);
  const nullDate = new Date('2000-01-01'); // api can't do null, so is using this date as null value. Actual null value is 1999
  return (kickoffDate > nullDate);
}

export { initPovOutcomes, useFetchPov, isPovStarted, fetchPov };